import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

interface CustomDialogProps {
  isOpen: boolean;
  title: string;
  message: string;
  type: "alert" | "prompt" | "confirm";
  defaultValue?: string;
  onConfirm: (value?: string) => void;
  onCancel: () => void;
  confirmText?: string;
  cancelText?: string;
  children?: React.ReactNode;
  hideFooter?: boolean;
}

export const CustomDialog: React.FC<CustomDialogProps> = ({
  isOpen,
  title,
  message,
  type,
  defaultValue = "",
  onConfirm,
  onCancel,
  confirmText,
  cancelText,
  children,
  hideFooter,
}) => {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState(defaultValue);
  const resolvedConfirmText = confirmText ?? t("common.dialog.ok");
  const resolvedCancelText = cancelText ?? t("common.dialog.cancel");

  React.useEffect(() => {
    if (isOpen) {
      setInputValue(defaultValue);
    }
  }, [isOpen, defaultValue]);

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onCancel();
      }}
    >
      <DialogContent showCloseButton={false} className="sm:max-w-[450px]">
        <DialogHeader>
          <DialogTitle className="text-[var(--color-accent-warning)] text-lg font-black uppercase tracking-wider">
            {title}
          </DialogTitle>
          <DialogDescription className="text-[#ccc] text-[0.95rem] leading-relaxed">
            {message}
          </DialogDescription>
        </DialogHeader>

        {type === "prompt" && (
          <Input
            autoFocus
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") onConfirm(inputValue);
              if (e.key === "Escape") onCancel();
            }}
            placeholder={t("common.dialog.placeholder")}
          />
        )}

        {children}

        {!hideFooter && (
          <DialogFooter className="bg-transparent border-t-0 mx-0 mb-0 p-0 flex-row justify-end">
            {(type === "confirm" || type === "prompt") && (
              <Button variant="outline" onClick={onCancel}>
                {resolvedCancelText}
              </Button>
            )}
            <Button
              onClick={() => onConfirm(type === "prompt" ? inputValue : undefined)}
              variant={type === "alert" ? "default" : "default"}
              className={
                type !== "alert"
                  ? "bg-[var(--color-accent-success)] text-black hover:bg-[var(--color-accent-success)]/90"
                  : ""
              }
            >
              {resolvedConfirmText}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};
